import React, { Fragment, useState, useEffect } from 'react';

import icons from './../../../icons.svg';
import {
  isScaleShelfType,
  countItemsAtSlot,
} from '../../../utils/shelf-config';
import MultipleProdutsSlots from '../modals/MultipleProductsSlots';

const ScaleHybridCabinetPlanogram = ({
  cabinetActions,
  planogramData,
  allProducts = [],
  t,
  cabinet,
  slotSelected,
}) => {
  const [openSlotInfoOverlay, setOpenSlotInfoOverlay] = useState({
    open: false,
    slotData: {},
    shelfNumber: null,
  });

  useEffect(() => {
    if (cabinetActions && cabinetActions.handleFetchCabinetInventory)
      cabinetActions.handleFetchCabinetInventory(cabinet.deviceCode);
    // fetch the inventory snapshot once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * handle slot selection differently depending upon calibration status
   * @param {*} shelf shelf selected
   * @param {*} slot slot selected
   * @param {*} product selected product
   */
  const handleSlotClickInPlanogram = (
    shelfNumber,
    selectedSlotData,
    product
  ) => {
    if (cabinet.calibration === 'on' && isScaleShelf(shelfNumber)) {
      slotSelected({
        shelfNumber: shelfNumber,
        slotData: selectedSlotData,
        product,
      });
    } else {
      setOpenSlotInfoOverlay({
        open: true,
        slotData: selectedSlotData,
        shelfNumber,
      });
    }
  };

  /**
   * check if the shelf number is a scale shelf
   * @param {*} shelfNumber
   * @returns
   */
  const isScaleShelf = shelfNumber =>
    planogramData.shelves.find(
      shelf => isScaleShelfType(shelf.type) && shelf.shelf === shelfNumber
    );

  return (
    <Fragment>
      <MultipleProdutsSlots
        isOpen={openSlotInfoOverlay.open}
        close={() =>
          setOpenSlotInfoOverlay({
            open: false,
            slotData: {},
            shelfNumber: null,
          })
        }
        cabinetActions={cabinetActions}
        cabinetSlotData={openSlotInfoOverlay.slotData}
        shelfNumber={openSlotInfoOverlay.shelfNumber}
        allProducts={allProducts}
        t={t}
        cabinet={cabinet}
        scaleShelves={
          planogramData.shelves.filter(shelf => isScaleShelfType(shelf.type)) ||
          []
        }
      />
      <div className="planogram__cabinet">
        <div className="planogram__cabinet-shelves">
          {(() => {
            const allShelves = [];
            // for each shelf data, draw slot in the planogram
            planogramData.shelves.forEach(shelf => {
              allShelves.push(
                <div
                  className={`planogram__cabinet-shelf ${
                    shelf.type === 'rfid'
                      ? 'planogram__cabinet-shelf--rfid'
                      : ''
                  }`}
                  key={`shelf-${shelf.shelf}`}
                >
                  <div className="planogram__slots">
                    {(() => {
                      // loop throgh all the slots in the particular shelf
                      const allSlots = [];
                      shelf.slots &&
                        shelf.slots.forEach(slot => {
                          //the product assinged to the slot during calibration
                          const assignedProductId = slot.productId;

                          //the product planned in the planogram
                          const planogramProductId =
                            slot.products && Array.isArray(slot.products)
                              ? slot.products[0]
                              : null;

                          const selectedAssignedProduct = allProducts.find(
                            product => product.id === assignedProductId
                          );

                          const selectedPlanogramProduct = allProducts.find(
                            product => product.id === planogramProductId
                          );
                          // scale2: the planogram slot carries the device's trusted count and is re-uploaded on
                          // every refill door close; the inventory snapshot is only a fallback until it arrives
                          const laneCount = Number.isInteger(slot.currentCount)
                            ? slot.currentCount
                            : countItemsAtSlot(
                                cabinet.currentItems,
                                shelf.shelf,
                                slot.slot
                              );
                          // scale1: display the planned product first, then the assigned one.
                          // scale2: the assigned product is what the device reports on the lane, so show it first
                          // and fall back to the plan for an empty lane; a plan/actual mismatch keeps its overlay.
                          const productToRender =
                            shelf.type === 'scale2'
                              ? selectedAssignedProduct ||
                                selectedPlanogramProduct ||
                                null
                              : selectedPlanogramProduct
                              ? selectedPlanogramProduct
                              : selectedAssignedProduct
                              ? selectedAssignedProduct
                              : null;

                          //if slot has no product assined or planned, just show the scale icon
                          if (!productToRender) {
                            allSlots.push(
                              <div
                                className="planogram__slots-item"
                                key={`shelf-${slot.slot}`}
                                onClick={() =>
                                  handleSlotClickInPlanogram(shelf.shelf, {
                                    slot: slot.slot,
                                    productAssigned: '',
                                    products: [],
                                    laneId: slot.laneId,
                                    currentCount: slot.currentCount,
                                    stockedCount: slot.stockedCount,
                                    confidence: slot.confidence,
                                  })
                                }
                              >
                                {/* the scale icon marks an empty weight lane; an empty RFID slot stays blank */}
                                {isScaleShelfType(shelf.type) && (
                                  <svg className="planogram__slots-item-cube">
                                    <use
                                      xlinkHref={`${icons}#icon-balance-scale`}
                                    ></use>
                                  </svg>
                                )}
                              </div>
                            );
                          }

                          // compare two different products
                          // are the planogram product and product calibrated same for the slot?
                          const sameProduct =
                            selectedAssignedProduct?.id ===
                            selectedPlanogramProduct?.id;

                          if (productToRender) {
                            allSlots.push(
                              <div
                                className={`planogram__slots-item`}
                                key={`slot-${shelf.shelf}-${slot.slot}-${productToRender.id}`}
                                onClick={() =>
                                  handleSlotClickInPlanogram(
                                    shelf.shelf,
                                    {
                                      slot: slot.slot,
                                      products: planogramProductId
                                        ? [planogramProductId]
                                        : [],
                                      productAssigned: assignedProductId,
                                      laneId: slot.laneId,
                                      currentCount: slot.currentCount,
                                      stockedCount: slot.stockedCount,
                                      confidence: slot.confidence,
                                    },
                                    assignedProductId
                                  )
                                }
                              >
                                <div className="planogram__slots-item-product">
                                  {productToRender.imageUrl ? (
                                    <img
                                      src={productToRender.imageUrl}
                                      alt="product"
                                    />
                                  ) : (
                                    <svg className="planogram__slots-item-cube">
                                      <use
                                        xlinkHref={`${icons}#icon-cube`}
                                      ></use>
                                    </svg>
                                  )}
                                  {shelf.type === 'scale2' &&
                                    Number.isInteger(laneCount) && (
                                      <span className="planogram__slots-item-count">
                                        {laneCount}
                                      </span>
                                    )}
                                </div>

                                {selectedPlanogramProduct &&
                                  !selectedAssignedProduct &&
                                  isScaleShelfType(shelf.type) && (
                                    <svg className="planogram__slots-item-cube planogram__slots-item-cube--overlay">
                                      <use
                                        xlinkHref={`${icons}#icon-balance-scale`}
                                      ></use>
                                    </svg>
                                  )}

                                {((!selectedPlanogramProduct &&
                                  selectedAssignedProduct &&
                                  isScaleShelfType(shelf.type)) ||
                                  (selectedPlanogramProduct &&
                                    selectedAssignedProduct &&
                                    !sameProduct)) && (
                                  <svg className="planogram__slots-item-cube planogram__slots-item-cube--overlay planogram__slots-item-cube--warn">
                                    <use
                                      xlinkHref={`${icons}#icon-warning`}
                                    ></use>
                                  </svg>
                                )}
                              </div>
                            );
                          }
                        });
                      return allSlots;
                    })()}
                  </div>
                </div>
              );
            });
            return allShelves;
          })()}
        </div>
      </div>
    </Fragment>
  );
};

export default ScaleHybridCabinetPlanogram;
